import {createAsyncThunk, createEntityAdapter, createSlice} from '@reduxjs/toolkit';
import {startLoading, stopLoading} from 'app/store/utilSlice';
import {apiDel, apiGet, apiPost, apiPut} from 'app/shared-components/util/restAPI';
import {fetchFeedPosts} from 'app/store/homeSlice';

const emptyArray= [];
const articleAdapter = createEntityAdapter({
	sortComparer: (a, b) => (b.publishDate || b.createdAt) - (a.publishDate || a.createdAt)
});

export const fetchArticle = createAsyncThunk(
  "articles/fetchArticle",
  async (id, { dispatch }) => {
	dispatch(startLoading());
	try{
      const apiName = 'CoreAPI3';
	  const path = '/x3/article/' + id;
      const options = {
		  headers: {},
		  response: false, // OPTIONAL (return the entire Axios response object instead of only response.data)
	  };

	  const data= await apiGet(apiName, path, options);
	  return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
);

export const fetchArticles = createAsyncThunk(
  "articles/fetchArticles",
  async ({bulk, channelId, refresh}, { dispatch, getState }) => {
	dispatch(startLoading());
	try{
		  const {user,articles} = getState();
		  let data= bulk;
		  if (!data){ //pre-fetched data via bulk api
		      //Check if we need to init the org articles
			  if (channelId){
				  if (!articles.orgMap[channelId]) await dispatch(initOrgArticles(channelId));
			  }

			  const apiName = 'CoreAPI3';
			  const path = '/x3/article/byowner/' + (channelId ? ('Channel:' + channelId) : ('User:' + user.id));
		      const options = {
				  headers: {},
				  response: false, // OPTIONAL (return the entire Axios response object instead of only response.data)
			  };
			  data= await apiGet(apiName, path, options);
		 }

	     return data;
	}finally{
	  dispatch(stopLoading());
	}
  }
)

export const createArticle = createAsyncThunk(
  "articles/createArticle",
  async (params, { dispatch, getState }) => {
	  try{
	      const apiName = 'CoreAPI3';
		  const path = '/x3/article/';
		  let body= { ...params};
	      const options = {
			  headers: {},
			  body: body,
			  response: false, // OPTIONAL (return the entire Axios response object instead of only response.data)
		  };

		 let article= await apiPut(apiName, path, options);
		 return article;
	 }finally{
		 dispatch(stopLoading());
	 }
  }
)

export const updateArticle = createAsyncThunk(
  "articles/updateArticle",
  async (params, { dispatch, getState }) => {
	  try{
		  const state = getState();
		  const existingArticle = findArticleEntry(state.articles, params.id);
		  const wasPublished = existingArticle?.status === 'Published';
		  const willBePublished = params.status === 'Published';
		  
	      const apiName = 'CoreAPI3';
		  const path = '/x3/article/' + params.id;
		  let body= { 
			  ...params,
			  id: undefined,
		  };
	      const options = {
			  headers: {},
			  body: body,
			  response: false, // OPTIONAL (return the entire Axios response object instead of only response.data)
		  };

		 let data= await apiPut(apiName, path, options);
		 
		 // If status changed to Published, update feed after delay
		 if (!wasPublished && willBePublished) {
			 setTimeout(() => {
				 dispatch(fetchFeedPosts({}));
			 }, 5000);
		 }
		 
		 return data;
	 }finally{
		 dispatch(stopLoading());
	 }
  }
)

export const deleteArticle = createAsyncThunk(
  "articles/deleteArticle",
  async (params, { dispatch }) => {
	  try{
	      const { articleId, channelId } = params;
	      const apiName = 'CoreAPI3';
		  const path = '/x3/article/' + articleId;
	      const options = {
			  headers: {},
			  response: false, // OPTIONAL (return the entire Axios response object instead of only response.data)
		  };

		 let data= await apiDel(apiName, path, options);
		 return data;
	 }finally{
		 dispatch(stopLoading());
	 }
  }
)

export const changeArticleOwner = createAsyncThunk(
	"articles/changeArticleOwner",
	async (params, { dispatch, getState }) => {
		try{
			const apiName = 'CoreAPI3';
			const {articleId, newOwnerId}= params;
			const path = '/x3/article/' + articleId + '/updateowner';
			let body= { newOwnerId };
			const options = {
				headers: {},
				body: body,
				response: false,
			};
			return await apiPost(apiName, path, options);
		}finally{
			dispatch(stopLoading());
		}
	}
)

export const fetchSystemArticles = createAsyncThunk(
  "articles/fetchSystemArticles",
  async ({ startfrom, append } = {}, { dispatch }) => {
    dispatch(startLoading());
    try {
      const path = '/x3/article/system' + (startfrom ? `?startfrom=${startfrom}` : '');
      const data = await apiGet('CoreAPI3', path, { headers: {}, response: false });
      // data = { articles: [...], lastId: '...' }
      return { articles: data.articles || data, lastId: data.lastId || null, append: !!append };
    } finally {
      dispatch(stopLoading());
    }
  }
)

const newOrganization= (channelId) => {
	return {
		id: channelId, unread: false,
		orgArticles: articleAdapter.getInitialState({status: "idle"}),
	};
}

const newArticle= (data) => {
	return {...data, unread: false};
}

//Gets appropriate entity state depending on presence of channelId
const getArticlesEntityState= (state, channelId) => {
	if (channelId) return state.orgMap[channelId].orgArticles;
	else return state.userArticles;
}

const findArticleEntry = (state, articleId) => {
	const articleEntry= state.userArticles.entities[articleId];
	if (articleEntry) return articleEntry;

	// Get all organizations
	const organizations = state.orgMap;

	// Search through all organizations
	for (const orgId in organizations) {
		const org = organizations[orgId];
		// Check if this organization has the orgArticles entity
		if (org.orgArticles?.entities) {
			// Look for the article in this organization's orgArticles
			const orgArticleEntry = org.orgArticles.entities[articleId];
			if (orgArticleEntry) return orgArticleEntry;
		}
	}

	return null;
}

const articlesSlice = createSlice({
  name: "articles",
  initialState: {
	  orgMap: {},
	  userArticles: articleAdapter.getInitialState(),
	  systemArticles: [],
	  systemArticlesLastId: null,
	  createAsSystem: false,
	  viewingArticleId: null,
	  selectedArticlesTab: 0,
	  commentPanelPostId: null,
	  articleDialogId: null,
	  unread: false
  },
  reducers: {
    setViewingArticleId: (state, action) => {
      state.viewingArticleId= action.payload;
    },
    setArticleDialogId: (state, action) => {
      state.articleDialogId = action.payload;
    },
    setCreateAsSystem: (state, action) => {
      state.createAsSystem = action.payload;
    },
  	setCommentPanelPostId: {
		  reducer: (state, action) => {
			  state.commentPanelPostId = action.payload;
		  }
  	},
    setSelectedArticlesTab: {
      reducer: (state, action) => {
        state.selectedArticlesTab = action.payload;
      }
    },
	  initOrgArticles: {
		  reducer: (state, action) => {
			  const channelId= action.payload;
			  if (!state.orgMap[channelId]){
				  state.orgMap[channelId]= newOrganization(channelId);
			  }
		  }
	  },
  },
  extraReducers: builder => {
    builder
    .addCase(fetchArticle.fulfilled, (state, action) => {
		const article= newArticle(action.payload);
		const entityState= getArticlesEntityState(state, article.channelId);
		articleAdapter.setOne(entityState, article);
    })
    .addCase(fetchArticles.fulfilled, (state, action) => {
		const orgId= action.meta.arg.channelId;
		const articleEntries = action.payload.map(article => {
           return newArticle(article);
        });
		const entityState= getArticlesEntityState(state, orgId);
        articleAdapter.setMany(entityState, articleEntries);
    })
    .addCase(createArticle.fulfilled, (state, action) => {
		const article= action.payload;
		const entityState= getArticlesEntityState(state, article.channelId);
        articleAdapter.addOne(entityState, newArticle(article));
    })
    .addCase(updateArticle.fulfilled, (state, action) => {
		const article= action.payload;
		const entityState= getArticlesEntityState(state, article.channelId);
		articleAdapter.upsertOne(entityState, article);
    })
	.addCase(changeArticleOwner.fulfilled, (state, action) => {
		const article= action.payload;
		const entityState= getArticlesEntityState(state, article.channelId);
		articleAdapter.upsertOne(entityState, article);
	})
    .addCase(fetchSystemArticles.fulfilled, (state, action) => {
		if (action.payload.append) {
			state.systemArticles = [...state.systemArticles, ...action.payload.articles];
		} else {
			state.systemArticles = action.payload.articles;
		}
		state.systemArticlesLastId = action.payload.lastId;
    })
    .addCase(deleteArticle.fulfilled, (state, action) => {
      	const articleId = action.meta.arg.articleId;
		const channelId = action.meta.arg.channelId;
		const entityState= getArticlesEntityState(state, channelId);
     	articleAdapter.removeOne(entityState, articleId);
    })
  }
})

const { selectById, selectAll } = articleAdapter.getSelectors();

export const selectArticleById = (articleId) => state => {
	if (articleId) return findArticleEntry(state.articles, articleId);
	return null;
}

export const selectAllArticles = () => state => {
	return selectAll(state.articles.userArticles);
}

export const selectArticlesByOrgChannelId = (channelId) => state => {
	if (channelId){
		const org= state.articles.orgMap[channelId];
		if (org) return selectAll(org.orgArticles);
	}
	return emptyArray;
}

export const selectSelectedArticlesTab = () => state => {
	return state.articles.selectedArticlesTab;
}
export const selectCommentPanelPostId = () => state => {
	return state.articles.commentPanelPostId;
}
export const selectArticlesUnread = ({ articles }) => articles.unread;
export const selectViewingArticleId = ({ articles }) => articles.selectViewingArticleId;
export const selectArticleDialogId = ({ articles }) => articles.articleDialogId;
export const selectCreateAsSystem = ({ articles }) => articles.createAsSystem;
export const selectSystemArticles = ({ articles }) => articles.systemArticles;
export const selectSystemArticlesLastId = ({ articles }) => articles.systemArticlesLastId;
export const { setCommentPanelPostId, initOrgArticles, setSelectedArticlesTab, setArticleDialogId, setCreateAsSystem } = articlesSlice.actions;

export default articlesSlice.reducer;